import { ApmBase, init as initApm } from '@elastic/apm-rum';

let apm: ApmBase | null = null;
let environment = 'dev';

/**
 * Initializes APM agent with given `config`.
 *
 * @param arg Config to initialize APM.
 */
const init = (
    config: AgentConfigOptions & { environment: 'dev' | 'development' | 'production' | 'prod' }
): void => {
    apm = initApm(config);
    environment = config.environment;
};

/**
 * Returns given `operation` with provided APM instance.
 * If APM is not initialized Error is displayed.
 *
 * @param operation Operation that awaits APM instance.
 */
const apmProvider =
    <T extends (...args: unknown[]) => unknown>(operation: (apm: ApmBase) => T) =>
    (...params: Parameters<T>): void => {
        if (apm) {
            operation(apm)(...params);
        } else {
            console.error('APM is not initialized\n', ...params);
        }
    };

/**
 * Operation for setting context for given `apm` instance.
 *
 * @param apm APM instance.
 */
const setContextOperation =
    (apm: ApmBase) =>
    (arg: Record<string, unknown>): void => {
        apm.setCustomContext(arg);
    };

/**
 * Returns true when yeself-debug is tru in localStorage or NODE_ENV is no production.
 */
const isDebugEnabled = (): boolean =>
    localStorage.getItem('yeself-debug') === 'true' || process.env.NODE_ENV !== 'production';

/**
 * Operation for error logging for given `apm` instance.
 *
 * @param apm APM instance.
 */
const errorOperation =
    (apm: ApmBase) =>
    (error: Error | string, ...messages: unknown[]): void => {
        if (environment === 'dev' || environment === 'development') {
            console.error(error);
        } else {
            apm.captureError(error);
        }

        messages.forEach((message) => {
            console.error(message);
        });
    };

const debugOperation = (...messages: unknown[]): void => {
    if (isDebugEnabled()) {
        console.debug(...messages);
    }
};

const sendDebug =
    (apm: ApmBase) =>
    (state): void => {
        apm.captureError(state);
    };

const { info } = console;

const Log = {
    /**
     * Initializes Logging utility with given `config`.
     */
    init,
    /**
     * Logs given Error or string as error.
     * Any additional given `messages` are displayed in console.
     */
    error: apmProvider(errorOperation),
    debug: debugOperation,
    info,
    setContext: apmProvider(setContextOperation),
    sendDebug: apmProvider(sendDebug),
    isDebugEnabled,
};

export default Log;
